import { SxProps } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { useEntitySecurityEvaluator } from '../../auth/Security';
import { useStyling } from '../../Theme';
import type { ActionType, SecuredActionType } from '../ActionType';
import { InlineResponsiveActions } from './InlineResponsiveActions';
import { PopoverResponsiveActions } from './PopoverResponsiveActions';

export interface ResponsiveActionsProps {
    /**
     * The list of secured actions to be displayed in the component.
     * Pass a **memoized** array of actions to avoid unnecessary re-renders.
     * Actions with a `securityAction` are filtered through the centralized ABAC framework.
     */
    securedActions?: SecuredActionType[];

    /**
     * Whether the component is activated or not. If false, the component will not render any actions.
     * This prop only applies to the inline variant of the component. It is intended to be used to only show the
     * actions menu on hover, for instance.
     */
    isActivated: boolean;

    /**
     * Whether to show a border around the component.
     * This prop only applies to the inline variant of the component.
     *
     * @default false
     */
    showBorder?: boolean;

    /**
     * The sx prop to apply to the container of the component.
     * In the case of the compact mode, this prop will be applied to the IconButton that opens the mobile menu.
     */
    containerSx?: SxProps;

    /**
     * The sx prop to apply to the icons with the individual action components - IconButtons or MenuItems.
     */
    iconSx?: SxProps;

    /**
     * Whether to force the component to render in compact mode regardless of the screen size.
     * When true, the component renders a popover menu with the actions instead of displaying them inline.
     *
     * @default false
     */
    forceCompactMode?: boolean;
}

/**
 * A component that displays a list of actions in a responsive manner.
 * On large view ports the actions are displayed as a horizontal inline list of icon buttons.
 * On smaller view ports the actions are displayed as a popover menu.
 * The popover menu variant can be forced irrespective of the screen size if required.
 *
 * Actions that declare a `securityAction` are filtered through the centralized ABAC framework.
 */
export default function ResponsiveActions(props: ResponsiveActionsProps) {
    const {
        securedActions = [],
        isActivated,
        showBorder = false,
        containerSx,
        iconSx,
        forceCompactMode = false
    } = props;
    const { isMobile } = useStyling();
    const shouldShowPopoverResponsiveActions = forceCompactMode || isMobile;
    const evaluateEntitySecurity = useEntitySecurityEvaluator();
    const resolvedActions = useMemo((): ActionType[] => {
        return securedActions.filter((action) => {
            if (action.hidden) {
                return false;
            }
            if (!action.securityAction) {
                return true;
            }

            const decision = evaluateEntitySecurity([action.securityAction], {
                entityName: action.entityName,
                entitySecurity: action.entitySecurity,
                featureName: action.featureName
            });

            return Boolean(decision.allowed[0] && !decision.decisions[0]?.pending);
        });
    }, [evaluateEntitySecurity, securedActions]);

    if (resolvedActions.length === 0) {
        return null;
    }

    return (
        <Fragment>
            {shouldShowPopoverResponsiveActions ? (
                <PopoverResponsiveActions
                    actions={resolvedActions}
                    containerSx={containerSx}
                    iconSx={iconSx}
                />
            ) : (
                <InlineResponsiveActions
                    actions={resolvedActions}
                    containerSx={containerSx}
                    showBorder={showBorder}
                    iconSx={iconSx}
                    isActivated={isActivated}
                />
            )}
        </Fragment>
    );
}
